/**
 * HTTP-status helpers for API failures.
 *
 * `makeCall` rejects with the parsed error body and attaches the HTTP status as
 * `statusCode`. Business outcomes must be branched on that status, never on the
 * message text — the per-seat billing flows rely on 402 / 406 / 409 meaning
 * three very different things.
 */

/** Statuses the API uses to signal a specific outcome rather than a failure. */
export const apiStatus = {
  /** Card declined — nothing was created or changed server-side. */
  paymentRequired: 402,
  /** Plan's user limit reached — guide the account to an upgrade. */
  seatLimitReached: 406,
  /** Downgrade needs the customer to choose which users to keep. */
  seatReductionRequired: 409,
  /** The session is no longer valid -- sign in again. */
  unauthorized: 401,
  /** Asked too often; the body's `retryAfter` says how long to wait, in seconds. */
  tooManyRequests: 429,
} as const;

/**
 * HTTP status behind a rejected API call.
 *
 * Falls back to the error envelope's `code`, which mirrors the status on
 * failures. The `>= 100` guard filters out the success envelope's `code: 0`.
 */
export const getApiStatus = (error: any): number | undefined => {
  const candidates = [error?.statusCode, error?.status, error?.code];
  for (const candidate of candidates) {
    const status = Number(candidate);
    if (Number.isFinite(status) && status >= 100 && status < 600) return status;
  }
  return undefined;
};

/** Card declined; the seat was not added and nothing was charged. */
export const isPaymentRequired = (error: any) =>
  getApiStatus(error) === apiStatus.paymentRequired;

/** The plan's user limit is already reached. */
export const isSeatLimitReached = (error: any) =>
  getApiStatus(error) === apiStatus.seatLimitReached;

/** The plan switch needs a seat-reduction selection first. */
export const isSeatReductionRequired = (error: any) =>
  getApiStatus(error) === apiStatus.seatReductionRequired;
